/**
 * search.ts — semantic search over courses + library collections.
 *
 * We have no vector store, so this is an LLM ranking pass: send the query plus
 * a compact list of items (title + short description) to a fast Claude model
 * and get back the relevant item indices ordered by relevance. Matching is by
 * MEANING, not just substring (e.g. "money" → "Financial Planning", "leading a
 * team" → "Leadership Essentials"). Callers fall back to substring matching if
 * this returns nothing (empty query, API error, or genuinely no matches).
 */
import { createMessage } from './aiProvider'

export interface SearchItem {
  id: string
  kind: 'content' | 'library'
  title: string
  description?: string
}

const SYSTEM = `You are a search relevance engine for a corporate training platform. You are given a user query and a numbered list of items — "content" is an individual course, "library" is a curated collection of courses.

Return the item numbers that are semantically relevant to the query, ordered most-relevant first. Match by MEANING and topic, not just exact words:
- "money" or "budget" → a "Financial Planning" course
- "leading a team" or "manager" → "Leadership" / "Delegation" content
- "onboarding" → "New Employee" collections

Only include genuinely relevant items. Respond with ONLY a JSON array of integers (the item numbers), e.g. [3,0,7]. If nothing is relevant, respond with []. No prose, no code fences.`

/**
 * Rank items by semantic relevance to the query. Returns the ids of relevant
 * items, most-relevant first. Empty array on no query / no match / failure.
 */
export async function semanticSearch(query: string, items: SearchItem[]): Promise<string[]> {
  const q = query.trim()
  if (!q || items.length === 0) return []

  const list = items
    .map((it, i) => {
      const desc = it.description?.trim() ? ` — ${it.description.trim().slice(0, 180)}` : ''
      return `${i}. [${it.kind}] ${it.title}${desc}`
    })
    .join('\n')

  let raw: string
  try {
    raw = await createMessage({
      system: SYSTEM,
      messages: [{ role: 'user', content: `Query: "${q}"\n\nItems:\n${list}` }],
      max_tokens: 400,
      model: 'claude-haiku-4-5-20251001',
    })
  } catch (err) {
    console.error('[search] semanticSearch failed:', err)
    return []
  }

  const match = raw.match(/\[[\s\S]*\]/)
  if (!match) return []
  try {
    const idxs = JSON.parse(match[0]) as unknown[]
    return idxs
      .filter((i): i is number => Number.isInteger(i) && (i as number) >= 0 && (i as number) < items.length)
      .map(i => items[i].id)
  } catch {
    return []
  }
}
